import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { colors, fonts, spacing } from '../../constants';
import { getRegionColor, STATUS_CARD_CONFIG } from '../../constants/raiseQueryPage';
import StatusCard from './StatusCard';

/**
 * RegionCard Component
 * Individual region card with status cards
 */
const RegionCard = ({
  regionOption,
  regionStats,
  edUser,
  isSelected,
  selectedStatusCard,
  onClick,
  onStatusCardClick,
  tableContainerRef,
}) => {
  const regionCode = regionOption.value;
  const regionLabel = regionOption.label;
  const edName = edUser ? edUser.name : 'N/A';
  const regionColor = getRegionColor(regionCode);

  return (
    <Paper
      key={regionCode}
      elevation={0}
      onClick={onClick}
      sx={{
        borderRadius: { xs: '12px', md: '10px', lg: '8px' },
        border: isSelected ? `2.5px solid ${regionColor}` : '1.5px solid #e5e7eb',
        borderTop: { xs: `3px solid ${regionColor}`, md: `2.5px solid ${regionColor}`, lg: `2px solid ${regionColor}` },
        p: { xs: '10px', md: '8px', lg: '6px' },
        pb: { xs: '12px', md: '10px', lg: '8px' },
        backgroundColor: isSelected ? `${regionColor}0f` : colors.backgroundWhite,
        cursor: 'pointer',
        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isSelected 
          ? `0 0 0 3px ${regionColor}20, 0 0 20px ${regionColor}40, 0 8px 32px ${regionColor}30` 
          : '0 2px 8px rgba(0, 0, 0, 0.06)',
        transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: isSelected 
            ? `0 0 0 4px ${regionColor}30, 0 0 30px ${regionColor}50, 0 10px 40px ${regionColor}40`
            : `0 8px 28px rgba(0, 0, 0, 0.12)`,
        },
      }}
    >
      {/* Region Header - Centered */}
      <Box sx={{ textAlign: 'center', mb: { xs: '6px', md: '5px', lg: '4px' } }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: fonts.weightBold,
            fontSize: { xs: '0.875rem', md: '0.8125rem', lg: '0.75rem' },
            color: '#0f172a',
            mb: '1px',
            lineHeight: 1.2,
          }}
        >
          {regionLabel}
        </Typography>
      </Box>

      {/* User Info - Left Aligned */}
      {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '3px', md: '2px', lg: '2px' }, mb: { xs: '6px', md: '5px', lg: '4px' } }}>
        <PersonIcon
          sx={{
            fontSize: { xs: '12px', md: '11px', lg: '10px' },
            color: '#64748b',
            flexShrink: 0,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: { xs: '0.625rem', md: '0.5625rem', lg: '0.5rem' },
            color: '#64748b',
            fontWeight: fonts.weightMedium,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          ED Region - {edName}
        </Typography>
      </Box> */}

      {/* Total Projects and Total Queries Stats - Same Line, Left Aligned */}
      <Box sx={{ display: 'flex', gap: { xs: spacing.sm, md: spacing.xs, lg: spacing.xs }, mb: { xs: '8px', md: '6px', lg: '5px' }, flexWrap: 'wrap' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.6875rem', md: '0.65rem', lg: '0.6rem' },
            color: '#64748b',
            fontWeight: fonts.weightMedium,
            textAlign: 'left',
            lineHeight: 1.3,
          }}
        >
          Total Projects - <Box component="span" sx={{ fontWeight: fonts.weightBold, color: colors.primaryDark, fontSize: { xs: '0.75rem', md: '0.7rem', lg: '0.65rem' } }}>{regionStats.totalProjects || 0}</Box>
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.6875rem', md: '0.65rem', lg: '0.6rem' },
            color: '#64748b',
            fontWeight: fonts.weightMedium,
            textAlign: 'left',
            lineHeight: 1.3,
          }}
        >
          Total Queries - <Box component="span" sx={{ fontWeight: fonts.weightBold, color: colors.primaryDark, fontSize: { xs: '0.75rem', md: '0.7rem', lg: '0.65rem' } }}>{regionStats.total || 0}</Box>
        </Typography>
      </Box>
      
      {/* Inner Status Cards - 4 in one row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(4, 1fr)',
          },
          gap: { xs: '5px', md: '4px', lg: '3px' },
        }}
      >
        {STATUS_CARD_CONFIG.map((statusConfig) => {
          const isStatusSelected = isSelected && selectedStatusCard === statusConfig.label;
          return (
            <StatusCard
              key={statusConfig.key}
              statusConfig={statusConfig}
              count={regionStats[statusConfig.key] || 0}
              isSelected={isStatusSelected}
              onClick={(e) => {
                e.stopPropagation();
                onStatusCardClick(statusConfig.label, regionCode, tableContainerRef);
              }}
            />
          );
        })}
      </Box>
    </Paper>
  );
};

export default RegionCard;
